import React from 'react';
import './BadgePill.css';

const STYLE_ICONS = {
  beta_tester: '🚀',
  undefeated: '🏆',
};

/**
 * Build inline style from backend style_config when present.
 * style_config: { gradient_start?, gradient_end?, box_shadow?, border? }
 */
function getInlineStyle(styleConfig) {
  if (!styleConfig || typeof styleConfig !== 'object') return null;
  const style = {};
  const start = styleConfig.gradient_start;
  const end = styleConfig.gradient_end;
  if (start && end) {
    style.background = `linear-gradient(135deg, ${start}, ${end})`;
  } else if (start) {
    style.background = start;
  } else if (end) {
    style.background = end;
  }
  if (styleConfig.box_shadow) {
    style.boxShadow = styleConfig.box_shadow;
  }
  if (styleConfig.border) {
    style.border = styleConfig.border;
  }
  if (Object.keys(style).length === 0) return null;
  return style;
}

const BadgePill = ({
  slug,
  name,
  tier,
  icon,
  style: styleKey,
  style_config,
  description = '',
  size = 'normal',
  showTooltip = true,
  keyboardAccessible = false,
}) => {
  const label = tier > 1 ? `${name} · ${tier}` : name;
  const displayIcon =
    icon ||
    STYLE_ICONS[styleKey] ||
    STYLE_ICONS[slug === 'beta_tester' ? 'beta_tester' : slug === 'undefeated_single_elim' ? 'undefeated' : null] ||
    '⭐';
  const dataStyle =
    styleKey || (slug === 'beta_tester' ? 'beta_tester' : slug === 'undefeated_single_elim' ? 'undefeated' : 'default');
  const inlineStyle = getInlineStyle(style_config);

  const descTrimmed = typeof description === 'string' ? description.trim() : '';
  const tooltipBody =
    descTrimmed ||
    'Achievement you earn automatically when your account meets the criteria. See the Badges page for details.';

  const ariaParts = [name];
  if (tier > 1) ariaParts.push(`Tier ${tier}`);
  if (descTrimmed) ariaParts.push(descTrimmed);
  const ariaLabel = ariaParts.join('. ');

  const pill = (
    <span
      className={`badge-pill badge-pill--${size}`}
      data-style={inlineStyle ? undefined : dataStyle}
      style={inlineStyle || undefined}
    >
      <span className="badge-pill-icon" aria-hidden>
        {displayIcon}
      </span>
      {label}
    </span>
  );

  if (!showTooltip) {
    return (
      <span className="badge-pill-root" aria-label={ariaLabel}>
        {pill}
      </span>
    );
  }

  return (
    <span
      className="badge-pill-tooltip-host"
      aria-label={ariaLabel}
      tabIndex={keyboardAccessible ? 0 : undefined}
    >
      {pill}
      <span className="badge-pill-tooltip" role="tooltip">
        <span className="badge-pill-tooltip-title">{name}</span>
        {tier > 1 && <span className="badge-pill-tooltip-tier">Tier {tier}</span>}
        <p className="badge-pill-tooltip-body">{tooltipBody}</p>
      </span>
    </span>
  );
};

export default BadgePill;
